import React, { useMemo, useRef, useState, useCallback } from "react";
import {
  Box,
  TextField,
  InputAdornment,
  useTheme,
  IconButton,
  Tooltip,
  Typography,
  Button,
  ButtonGroup,
  Chip,
  CircularProgress,
  ClickAwayListener,
  Grow,
  Paper,
  Popper,
  MenuItem,
  MenuList,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import AddIcon from "@mui/icons-material/Add";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import RemoveIcon from "@mui/icons-material/Remove";
import { formatValue, calculateAndFormatNoAbsInt } from "../../component/Methods";
import { getEntityDateColumn } from "../../utils/entityDateColumn";

const buildStrategyChildRow = (parentRow, strategy, index) => {
  const parentId = parentRow.id;
  const strategyKey =
    strategy?.strategyId ?? strategy?.strategyName ?? index;
  return {
    id: `child::${parentId}::${strategyKey}::${index}`,
    isStrategyChild: true,
    parentId,
    abbreviation: "",
    name: strategy?.strategyName || "-",
    aum: strategy?.strategyAUM,
    isSMA: parentRow.isSMA,
    date: null,
    hasStrategyData: false,
  };
};

const AccountingTable = ({
  accountingData,
  searchTerm,
  onSearchChange,
  page,
  onPageChange,
  pageSize,
  onPageSizeChange,
  onEdit,
  onSyncAll,
  onSync,
  onAddeparSync,
  onSchwabSync,
  onSwapSync,
  onAdd,
  isSyncing = false,
  isAddeparSyncing = false,
  isSchwabSyncing = false,
  isSwapSyncing = false,
}) => {
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [sortModel, setSortModel] = useState([
    { field: "aum", sort: "desc" },
  ]);

  // Split button state (Sync Data dropdown)
  const [syncMenuOpen, setSyncMenuOpen] = useState(false);
  const syncAnchorRef = useRef(null);
  const [selectedSyncIndex, setSelectedSyncIndex] = useState(0);

  const syncOptions = [
    "Sync Accounting Data",
    "Sync Funds data from seamless",
    "Sync SMA data from Addepar",
    "Sync SMA data from Schwab",
    "Sync Swap data",
  ];

  const anySyncing =
    isSyncing || isAddeparSyncing || isSchwabSyncing || isSwapSyncing;

  const isSyncFetching =
    (selectedSyncIndex === 0 && anySyncing) ||
    (selectedSyncIndex === 1 && isSyncing) ||
    (selectedSyncIndex === 2 && isAddeparSyncing) ||
    (selectedSyncIndex === 3 && isSchwabSyncing) ||
    (selectedSyncIndex === 4 && isSwapSyncing);

  const handleSyncClick = () => {
    if (selectedSyncIndex === 0) {
      onSyncAll && onSyncAll();
      return;
    }
    handleSyncMenuItemClick(selectedSyncIndex);
  };

  const handleSyncMenuItemClick = (index) => {
    setSyncMenuOpen(false);
    setSelectedSyncIndex(index);
    switch (index) {
      case 0:
        // Trigger all syncs
        onSyncAll && onSyncAll();
        return;
      case 1:
        onSync && onSync();
        return;
      case 2:
        onAddeparSync && onAddeparSync();
        return;
      case 3:
        onSchwabSync && onSchwabSync();
        return;
      case 4:
        onSwapSync && onSwapSync();
        return;
      default:
        return;
    }
  };

  const handleSyncToggle = () => {
    setSyncMenuOpen((prev) => !prev);
  };

  const handleSyncClose = (event) => {
    if (
      syncAnchorRef.current &&
      syncAnchorRef.current.contains(event.target)
    ) {
      return;
    }
    setSyncMenuOpen(false);
  };

  const compareSortValues = useCallback((a, b) => {
    if (a == null && b == null) return 0;
    if (a == null || a === "") return -1;
    if (b == null || b === "") return 1;
    if (typeof a === "boolean" && typeof b === "boolean") {
      return Number(a) - Number(b);
    }
    if (typeof a === "number" && typeof b === "number") return a - b;
    const aNum =
      typeof a === "string" && a.trim() !== "" && !Number.isNaN(Number(a))
        ? Number(a)
        : null;
    const bNum =
      typeof b === "string" && b.trim() !== "" && !Number.isNaN(Number(b))
        ? Number(b)
        : null;
    if (aNum != null && bNum != null) return aNum - bNum;
    return String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }, []);

  const sortRowsByModel = useCallback(
    (rows, model) => {
      const effectiveModel =
        model?.length > 0
          ? model
          : [{ field: "aum", sort: "desc" }];
      const sorted = [...rows];
      [...effectiveModel].reverse().forEach(({ field, sort }) => {
        if (!field || !sort) return;
        const dir = sort === "desc" ? -1 : 1;
        sorted.sort((a, b) => {
          let aVal = a?.[field];
          let bVal = b?.[field];
          if (field === "isSMA") {
            aVal = a?.isSMA === true;
            bVal = b?.isSMA === true;
          }
          return compareSortValues(aVal, bVal) * dir;
        });
      });
      return sorted;
    },
    [compareSortValues]
  );

  const filteredData = useMemo(() => {
    let data = accountingData || [];
    if (searchTerm) {
      data = data.filter((item) =>
        item.abbreviation?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return data;
  }, [accountingData, searchTerm]);

  const rowsWithExpandInfo = useMemo(() => {
    return filteredData.map((item) => ({
      ...item,
      hasStrategyData: (item?.strategyAUMs || [])?.length > 0,
    }));
  }, [filteredData]);

  // Sort parents only, then nest expanded strategy children. Stamp __treeOrder
  // so DataGrid sort cannot float children above their parents.
  const flattenedRows = useMemo(() => {
    const topLevel = sortRowsByModel(
      rowsWithExpandInfo.filter((row) => !row?.isStrategyChild),
      sortModel
    );
    const list = [];
    topLevel.forEach((row) => {
      list.push(row);
      const rowId = row.id;
      const isExpanded =
        expandedRows.has(rowId) || expandedRows.has(String(rowId));
      if (isExpanded && row.hasStrategyData) {
        (row.strategyAUMs || []).forEach((strategy, index) => {
          list.push(buildStrategyChildRow(row, strategy, index));
        });
      }
    });
    return list.map((row, index) => ({ ...row, __treeOrder: index }));
  }, [rowsWithExpandInfo, expandedRows, sortModel, sortRowsByModel]);

  const treeOrderById = useMemo(() => {
    const map = new Map();
    flattenedRows.forEach((row) => {
      map.set(String(row.id), row.__treeOrder);
    });
    return map;
  }, [flattenedRows]);

  const getTreeOrderSortComparator = useCallback(
    () => (v1, v2, cellParams1, cellParams2) => {
      const o1 =
        treeOrderById.get(String(cellParams1?.id)) ?? Number.MAX_SAFE_INTEGER;
      const o2 =
        treeOrderById.get(String(cellParams2?.id)) ?? Number.MAX_SAFE_INTEGER;
      return o1 - o2;
    },
    [treeOrderById]
  );

  const columns = useMemo(() => {
    const defs = [
      {
        field: "__expand",
        headerName: "",
        width: 44,
        minWidth: 44,
        maxWidth: 44,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: (params) => {
          if (params.row.isStrategyChild) return null;
          if (!params.row.hasStrategyData) return null;
          const isExpanded =
            expandedRows.has(params.row.id) ||
            expandedRows.has(String(params.row.id));
          return (
            <IconButton
              size="small"
              onClick={(event) => {
                event.stopPropagation();
                setExpandedRows((prev) => {
                  const next = new Set(prev);
                  const key = params.row.id;
                  if (next.has(key) || next.has(String(key))) {
                    next.delete(key);
                    next.delete(String(key));
                  } else {
                    next.add(key);
                  }
                  return next;
                });
              }}
              sx={{ p: 0.25 }}
            >
              {isExpanded ? (
                <RemoveIcon fontSize="small" />
              ) : (
                <AddIcon fontSize="small" />
              )}
            </IconButton>
          );
        },
      },
      {
        field: "abbreviation",
        headerName: "Entity Abbreviation",
        flex: 1.5,
        minWidth: 50,
        renderCell: (params) =>
          params.row.isStrategyChild ? null : params.value,
      },
      {
        field: "name",
        headerName: "Entity Name",
        flex: 1.5,
        minWidth: 200,
      },
      {
        field: "isSMA",
        headerName: "Entity Type",
        flex: 1,
        minWidth: 120,
        renderCell: (params) => {
          if (params.row.isStrategyChild) return null;
          const isSMA = params.value === true;
          return (
            <Chip
              label={isSMA ? "SMA" : "Fund"}
              size="small"
              sx={{
                fontWeight: 600,
                fontSize: "0.75rem",
                color: isSMA
                  ? theme.palette.mode === "dark"
                    ? "#90caf9"
                    : "#1565c0"
                  : theme.palette.mode === "dark"
                    ? "#81c784"
                    : "#2e7d32",
                border: `1px solid ${
                  isSMA
                    ? theme.palette.mode === "dark"
                      ? "#42a5f5"
                      : "#bbdefb"
                    : theme.palette.mode === "dark"
                      ? "#66bb6a"
                      : "#c8e6c9"
                }`,
              }}
            />
          );
        },
      },
      {
        field: "aum",
        headerName: "AUM",
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const value = params.value;
          if (value === undefined || value === null) return "0.00";
          if (params.row.isStrategyChild) {
            return value < 0
              ? `(${calculateAndFormatNoAbsInt(Math.abs(value))})`
              : calculateAndFormatNoAbsInt(value);
          }
          return formatValue(value);
        },
      },
      (() => {
        const dateColumn = getEntityDateColumn({
          field: "date",
          headerName: "Creation Date",
          flex: 1,
          minWidth: 150,
        });
        return {
          ...dateColumn,
          renderCell: (params) =>
            params.row.isStrategyChild ? null : dateColumn.renderCell(params),
        };
      })(),
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => {
          if (params.row.isStrategyChild) return null;
          return (
            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip title="Edit">
                <IconButton
                  color="primary"
                  size="small"
                  onClick={() => onEdit && onEdit(params.row)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          );
        },
      },
    ];

    return defs.map((col) =>
      col.sortable === false
        ? col
        : { ...col, getSortComparator: getTreeOrderSortComparator }
    );
  }, [theme, expandedRows, onEdit, getTreeOrderSortComparator]);

  return (
    <Box sx={{  width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <TextField
            size="small"
            placeholder="Search accounting records..."
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: "text.disabled" }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 300,
              "& .MuiOutlinedInput-root": {
                borderRadius: 1,
              },
            }}
          />
        </Box>
        
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
            {filteredData.length} records
          </Typography>

          <ButtonGroup
            variant="contained"
            ref={syncAnchorRef}
            aria-label="sync data split button"
            sx={{
              borderRadius: 1.5,
              minWidth: 220,
              boxShadow: "none",
              overflow: "hidden",
              "& .MuiButton-root": {
                textTransform: "none",
                fontWeight: 600,
                fontSize: "14px",
                backgroundColor: "#7b1fa2",
                "&:hover": { backgroundColor: "#6a1b9a" },
              },
            }}
          >
            <Button
              onClick={handleSyncClick}
              disabled={
                isSyncing ||
                isAddeparSyncing ||
                isSchwabSyncing ||
                isSwapSyncing ||
                (!onSync && !onAddeparSync && !onSchwabSync && !onSwapSync)
              }
              startIcon={
                isSyncFetching ? (
                  <CircularProgress size={16} sx={{ color: "white" }} />
                ) : (
                  <SyncIcon />
                )
              }
            >
              {syncOptions[selectedSyncIndex]}
            </Button>
            <Button
              size="small"
              aria-controls={syncMenuOpen ? "sync-data-menu" : undefined}
              aria-expanded={syncMenuOpen ? "true" : undefined}
              aria-haspopup="menu"
              onClick={handleSyncToggle}
              disabled={
                isSyncing ||
                isAddeparSyncing ||
                isSchwabSyncing ||
                isSwapSyncing ||
                (!onSync && !onAddeparSync && !onSchwabSync && !onSwapSync)
              }
              sx={{ minWidth: 40 }}
            >
              <ArrowDropDownIcon />
            </Button>
          </ButtonGroup>

          <Popper
            sx={{ zIndex: 1 }}
            open={syncMenuOpen}
            anchorEl={syncAnchorRef.current}
            placement="bottom-start"
            role={undefined}
            transition
            disablePortal
          >
            {({ TransitionProps, placement }) => (
              <Grow
                {...TransitionProps}
                style={{
                  transformOrigin:
                    placement === "bottom" ? "center top" : "center bottom",
                }}
              >
                <Paper>
                  <ClickAwayListener onClickAway={handleSyncClose}>
                    <MenuList
                      id="sync-data-menu"
                      autoFocusItem
                      sx={{
                        "& .MuiMenuItem-root": {
                          fontSize: "14px !important",
                          minHeight: 40,
                        },
                      }}
                    >
                      {syncOptions.map((option, index) => {
                        const isDisabled =
                          (index === 0 && (!onSyncAll || anySyncing)) ||
                          (index === 1 && (isSyncing || !onSync)) ||
                          (index === 2 &&
                            (isAddeparSyncing || !onAddeparSync)) ||
                          (index === 3 &&
                            (isSchwabSyncing || !onSchwabSync)) ||
                          (index === 4 && (isSwapSyncing || !onSwapSync));
                        return (
                          <MenuItem
                            key={option}
                            selected={index === selectedSyncIndex}
                            disabled={isDisabled}
                            onClick={() => handleSyncMenuItemClick(index)}
                          >
                            {option}
                          </MenuItem>
                        );
                      })}
                    </MenuList>
                  </ClickAwayListener>
                </Paper>
              </Grow>
            )}
          </Popper>
          
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={onAdd}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 120,
              backgroundColor: theme.palette.success.main,
              "&:hover": {
                backgroundColor: theme.palette.success.dark,
              },
            }}
          >
            Add Item
          </Button>
        </Box>
      </Box>


      <DataGrid
        rows={flattenedRows}
        columns={columns}
        getRowId={(row) => row.id}
        sortModel={sortModel}
        onSortModelChange={(model) => setSortModel(model || [])}
        sortingMode="client"
        paginationModel={{
          page: page || 0,
          pageSize: Math.min(pageSize || 1000, 1000),
        }}
        onPaginationModelChange={(model) => {
          onPageChange?.(model.page);
          onPageSizeChange?.(Math.min(model.pageSize, 1000));
        }}
        pageSizeOptions={[100,200, 500, 1000]}
        pagination
        disableRowSelectionOnClick
        density="compact"
        getRowClassName={(params) =>
          params.row.isStrategyChild ? "accounting-strategy-child" : ""
        }
        sx={{
          border: "none",
          height: "calc(100vh - 20px)",
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.grey[100]
              : theme.palette.background.paper,
          "& .MuiDataGrid-main": {
            backgroundColor: "inherit",
          },
          "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
            color: theme.palette.text.primary,
            fontWeight: 500,
          },
          "& .MuiDataGrid-columnHeaders": {
            backgroundColor: theme.palette.grey[100],
            borderBottom: `2px solid ${theme.palette.divider}`,
          },
          "& .MuiDataGrid-columnHeader": {
            fontWeight: 600,
            color: theme.palette.text.primary,
          },
          "& .MuiDataGrid-row": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[100]
                : undefined,
          },
          "& .MuiDataGrid-row:hover": {
            backgroundColor: theme.palette.action.hover,
          },
          "& .MuiDataGrid-row.accounting-strategy-child": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(144, 202, 249, 0.06)"
                : theme.palette.grey[50],
          },
          "& .MuiDataGrid-virtualScroller": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[100]
                : undefined,
          },
          "& .MuiDataGrid-filler, & .MuiDataGrid-scrollbarFiller": {
            backgroundColor:
              theme.palette.mode === "dark"
                ? theme.palette.grey[100]
                : undefined,
          },
          "& .MuiDataGrid-footerContainer": {
            backgroundColor: theme.palette.grey[100],
            borderTop: `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </Box>
  );
};

export default AccountingTable;